import React, { useMemo, useState } from "react";
import { Radio, CalendarDays, Inbox, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Avatar, ConsultationRow, Meta, MoreMenu, actionsFor } from "./ConsultationRow";
import { fmtDateTime, groupByDay, typeLabel, fmtAmount } from "./consultationUi";

export function EmptyState({ title, body, testId }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center" data-testid={testId}>
      <Inbox size={22} className="mx-auto text-slate-300" />
      <p className="mt-3 text-sm font-extrabold text-[#0A2540]">{title}</p>
      <p className="mt-1 text-xs text-slate-500">{body}</p>
    </div>
  );
}

function SectionHeader({ icon: Icon, tone, title, count, hint, testId }) {
  return (
    <div className={`flex items-center gap-3 px-5 py-3.5 ${tone}`} data-testid={testId}>
      <Icon size={18} />
      <h2 className="text-base font-extrabold" style={{ fontFamily: "Poppins" }}>{title} ({count})</h2>
      <span className="text-sm text-slate-500 hidden sm:inline">{hint}</span>
    </div>
  );
}

/** Compact red "Live Now" band — uses only the already-loaded consultation (no timer, no polling). */
export function LiveNowSection({ items, handlers }) {
  return (
    <section className="rounded-2xl border border-rose-200 bg-rose-50/70 overflow-hidden" data-testid="live-now-section">
      <SectionHeader icon={Radio} tone="text-rose-700" title="Live Now" count={items.length} hint="Join your ongoing consultation" testId="live-now-header" />
      <ul className="px-4 pb-4 space-y-3">
        {items.map((c) => {
          const { primary, more } = actionsFor(c, handlers);
          return (
            <li key={c.id} className="bg-white rounded-xl border border-rose-100 px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid={`live-row-${c.id}`} data-status={c.status}>
              <Avatar name={c.user_name} size="h-14 w-14 text-lg" />
              <div className="min-w-[180px] flex-1">
                <p className="text-lg font-extrabold text-[#0A2540] leading-tight">{c.user_name || "Client"}</p>
                {c.query && <p className="text-sm text-slate-500 truncate">"{c.query}"</p>}
                <Meta c={c} className="mt-2" />
              </div>
              <div className="hidden md:block h-12 w-px bg-rose-100" />
              <div className="text-center min-w-[96px]" data-testid="live-elapsed">
                <p className="text-sm font-extrabold text-rose-600">{c.started_at ? fmtDateTime(c.started_at) : c.status === "waiting_for_lawyer" ? "Client is waiting" : "In progress"}</p>
                <p className="text-[11px] text-slate-500">{c.started_at ? "started" : "now"}</p>
              </div>
              <div className="flex items-center gap-2 ml-auto">{primary}<MoreMenu c={c} items={more} /></div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Upcoming bookings grouped client-side by IST day (Today → Tomorrow → later). */
export function UpcomingSection({ items, handlers }) {
  const groups = groupByDay(items);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="upcoming-section">
      <SectionHeader icon={CalendarDays} tone="text-[#0A2540] border-b border-slate-100" title="Upcoming" count={items.length} hint="Your upcoming consultations" testId="upcoming-header" />
      {items.length === 0 ? (
        <div className="p-4"><EmptyState title="No upcoming consultations" body="You don't have any upcoming consultations." testId="empty-upcoming" /></div>
      ) : groups.map((g) => (
        <div key={g.key} className="px-4 pt-4 pb-2" data-testid={`day-group-${g.key}`}>
          <div className="flex items-baseline gap-3 mb-3">
            <h3 className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{g.title}</h3>
            <span className="text-sm text-slate-500">{g.date}</span>
          </div>
          <ul className="space-y-2.5">{g.items.map((c) => <ConsultationRow key={c.id} c={c} dayTitle={g.title} handlers={{ ...handlers, inUpcoming: true }} />)}</ul>
        </div>
      ))}
    </section>
  );
}

/** Mobile (< md) stacked card for historical records — same data, no columns, text wraps. */
function RecordCard({ c, handlers }) {
  const { primary, more } = actionsFor(c, handlers);
  return (
    <li className="rounded-xl border border-slate-200 bg-white px-4 py-3.5" data-testid={`record-card-${c.id}`} data-status={c.status}>
      <p className="text-xs font-bold text-slate-500" data-testid="record-card-when">{fmtDateTime(c.slot_start_utc || c.started_at || c.created_at)}</p>
      <p className="mt-2 font-extrabold text-[#0A2540] break-words">{c.user_name || "Client"}</p>
      {c.query && <p className="text-sm text-slate-500 break-words">"{c.query}"</p>}
      <p className="mt-2 text-xs font-semibold text-slate-600">{typeLabel(c.consultation_type)} · {fmtAmount(c)} · {c.duration_minutes || 15} min</p>
      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{(c.status || "").replace(/_/g, " ")}</span>
        <MoreMenu c={c} items={more} />
      </div>
      {primary.length > 0 && <div className="mt-3 flex flex-wrap gap-2 [&>*]:h-9 [&>*]:px-3 [&>*]:whitespace-normal [&>*]:text-left">{primary}</div>}
    </li>
  );
}

/** Completed / cancelled / no-show: dense table on desktop, stacked cards below `md` (no horizontal scroll). */
// Sortable columns → accessor. Default: newest first (matches the server order).
const SORT_KEYS = {
  when: (c) => new Date(c.slot_start_utc || c.started_at || c.created_at || 0).getTime(),
  client: (c) => (c.user_name || "").toLowerCase(),
  type: (c) => typeLabel(c.consultation_type).toLowerCase(),
  duration: (c) => Number(c.duration_minutes) || 15,
  amount: (c) => Number(c.amount) || 0,
};
// [sortKey, label, fixed width px (null = flexible), className]. Fixed layout so the actions column can never collide with Status.
const COLUMNS = [
  ["when", "Date / time", 150, ""], ["client", "Client", 140, ""], [null, "Subject", null, ""], ["type", "Type", 72, ""],
  ["duration", "Duration", 84, ""], ["amount", "Amount", 84, ""], [null, "Status", 110, ""], [null, "", 230, ""],
];

export function sortRecords(items, { key, dir }) {
  const get = SORT_KEYS[key];
  if (!get) return items;
  const sign = dir === "asc" ? 1 : -1;
  return items.slice().sort((a, b) => { const x = get(a), y = get(b); return x < y ? -sign : x > y ? sign : 0; });
}

export function RecordsTable({ items, handlers, testId }) {
  const [sort, setSort] = useState({ key: "when", dir: "desc" });
  const sorted = useMemo(() => sortRecords(items, sort), [items, sort]);
  const toggle = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "client" || key === "type" ? "asc" : "desc" }));
  return (
    <>
      <ul className="md:hidden space-y-2.5" data-testid={`${testId}-mobile`}>
        {sorted.map((c) => <RecordCard key={c.id} c={c} handlers={handlers} />)}
      </ul>
      <section className="hidden md:block rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm table-fixed">
        <colgroup>{COLUMNS.map(([, h, w]) => <col key={h || "actions"} style={w ? { width: w } : undefined} />)}</colgroup>
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
            {COLUMNS.map(([key, h, , cls]) => (
              <th key={h || "actions"} className={`px-3 py-2.5 font-bold ${cls}`} aria-sort={key && sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
                {key ? (
                  <button type="button" onClick={() => toggle(key)} data-testid={`sort-${key}`}
                          className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-[#0A2540] ${sort.key === key ? "text-[#0A2540]" : ""}`}>
                    {h}{sort.key === key ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-50" />}
                  </button>
                ) : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sorted.map((c) => {
            const { primary, more } = actionsFor(c, { ...handlers, compact: true });
            return (
              <tr key={c.id} className="hover:bg-slate-50/70" data-testid={`record-row-${c.id}`} data-status={c.status}>
                <td className="px-3 py-2.5 whitespace-nowrap font-semibold text-slate-700 truncate" title={fmtDateTime(c.slot_start_utc || c.started_at || c.created_at)}>{fmtDateTime(c.slot_start_utc || c.started_at || c.created_at)}</td>
                <td className="px-3 py-2.5 font-extrabold text-[#0A2540] whitespace-nowrap truncate" title={c.user_name || "Client"}>{c.user_name || "Client"}</td>
                <td className="px-3 py-2.5 text-slate-500 truncate" title={c.query || ""}>{c.query || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{typeLabel(c.consultation_type)}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{c.duration_minutes || 15} min</td>
                <td className="px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap">{fmtAmount(c)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap"><span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{(c.status || "").replace(/_/g, " ")}</span></td>
                <td className="px-3 py-2.5 whitespace-nowrap"><div className="flex items-center justify-end gap-2">{primary}<MoreMenu c={c} items={more} /></div></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
    </>
  );
}
